/**
 * Money representation for Berrier Reno.
 *
 * Per the master specification (Section 8), all authoritative monetary values
 * are stored and calculated as INTEGER CENTS. Binary floating point must never
 * be used for authoritative financial values, so parsing and formatting are
 * done with string/integer arithmetic only.
 */

/** A monetary amount in whole US cents (integer). */
export type Cents = number;

export type MoneyParseResult =
  | { ok: true; cents: Cents | null }
  | { ok: false; error: string };

/**
 * Parse user input (e.g. "$1,234.56", "1234.5", "1234", "") into integer cents.
 * An empty/blank input parses to `null`, meaning "not entered".
 */
export function parseMoneyInput(raw: string): MoneyParseResult {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") {
    return { ok: true, cents: null };
  }
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) {
    return { ok: false, error: "Enter a dollar amount like 1250 or 1,250.75." };
  }
  const [, sign, whole, fraction] = match;
  const fracPadded = (fraction ?? "").padEnd(2, "0");
  const cents =
    Number.parseInt(whole, 10) * 100 + (fracPadded ? Number.parseInt(fracPadded, 10) : 0);
  if (!Number.isSafeInteger(cents)) {
    return { ok: false, error: "Amount is too large." };
  }
  return { ok: true, cents: sign ? -cents : cents };
}

/** Format integer cents for display, e.g. 123456 -> "$1,234.56". Null -> "—". */
export function formatCents(cents: Cents | null): string {
  if (cents === null || cents === undefined) {
    return "—";
  }
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toString();
  const fraction = (abs % 100).toString().padStart(2, "0");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}$${withCommas}.${fraction}`;
}

/** Format a signed variance, e.g. +$300.00 / -$460.00 / $0.00. */
export function formatSignedCents(cents: Cents): string {
  if (cents > 0) {
    return `+${formatCents(cents)}`;
  }
  return formatCents(cents);
}

/**
 * Convert stored cents back into a plain editable input string,
 * e.g. 123456 -> "1234.56", null -> "".
 */
export function centsToInputString(cents: Cents | null): string {
  if (cents === null || cents === undefined) {
    return "";
  }
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toString();
  const fraction = (abs % 100).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
